import React, { useEffect, useRef, useState } from "react";
import "~/css/OTPInput.css";

interface OTPInputProps {
  length?: number;
  onComplete: (code: string) => void;
  disabled?: boolean;
}

/**
 * OTP入力コンポーネント
 * 6つの個別入力フィールドで構成され、各フィールドに1文字のみ入力可能
 * 1つ入力すると次のフィールドにフォーカス、バックスペースで前のフィールドの値が初期化される
 */
export const OTPInput: React.FC<OTPInputProps> = ({ length = 6, onComplete, disabled = false }) => {
  const [codes, setCodes] = useState<string[]>(Array(length).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(length).fill(null));

  /**
   * マウント時に最初のフィールドにフォーカス
   */
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  /**
   * 全てのフィールドが入力されたかチェック
   */
  useEffect(() => {
    if (codes.every((code) => code !== "")) {
      const fullCode = codes.join("");
      onComplete(fullCode);
    }
  }, [codes]);

  /**
   * 入力値の変更を処理
   * @param index フィールドのインデックス
   * @param value 入力値
   */
  const handleInputChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // 数字のみ許可

    const newCodes = [...codes];

    if (value.length === 1) {
      // 1文字入力された場合
      newCodes[index] = value;
      setCodes(newCodes);

      // 次のフィールドにフォーカス
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (value.length === 0) {
      // 値が削除された場合
      newCodes[index] = "";
      setCodes(newCodes);
    }
  };

  /**
   * バックスペースの処理
   * @param index フィールドのインデックス
   * @param newCodes 現在のコード配列
   */
  const handleBackspace = (index: number, newCodes: string[]) => {
    if (codes[index] === "") {
      // 現在のフィールドが空の場合、前のフィールドの値を削除してフォーカス
      if (index > 0) {
        newCodes[index - 1] = "";
        setCodes(newCodes);
        inputRefs.current[index - 1]?.focus();
      }
    } else {
      // 現在のフィールドに値がある場合、その値を削除
      newCodes[index] = "";
      setCodes(newCodes);
    }
  };

  /**
   * キーダウンイベントを処理
   * @param index フィールドのインデックス
   * @param e KeyboardEvent
   */
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newCodes = [...codes];
      handleBackspace(index, newCodes);
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  /**
   * ペースト時の処理
   * @param index フィールドのインデックス
   * @param e ClipboardEvent
   */
  const handlePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const numericData = pastedData.replace(/\D/g, ""); // 数字のみ抽出

    if (numericData.length > 0) {
      const newCodes = [...codes];

      // ペーストされたデータを各フィールドに配置
      for (let i = 0; i < Math.min(numericData.length, length - index); i++) {
        newCodes[index + i] = numericData[i];
      }

      setCodes(newCodes);

      // 最後のフィールドまで埋まった場合、自動で送信
      // そうでない場合は、最後に入力したフィールドの次にフォーカス
      const nextIndex = Math.min(index + numericData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div className="otp-input-container">
      {codes.map((code, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={code}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          disabled={disabled}
          className="otp-input-field"
          aria-label={`認証コード ${index + 1} 文字目`}
        />
      ))}
    </div>
  );
};
